import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import useSilentMentor from '../hooks/useSilentMentor';
import SilentMentorNudge from './SilentMentorNudge';
import silentMentorApi from '../services/silentMentorApi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Local suppression helpers
function isSuppressed(nudgeId) {
  try {
    const key = `nudge_suppressed_${nudgeId}`;
    const stored = localStorage.getItem(key);
    if (!stored) return false;
    const { expiresAt } = JSON.parse(stored);
    if (Date.now() > expiresAt) {
      localStorage.removeItem(key);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
}

function suppressLocally(nudgeId, ttlMs) {
  try {
    const key = `nudge_suppressed_${nudgeId}`;
    localStorage.setItem(key, JSON.stringify({ expiresAt: Date.now() + ttlMs }));
  } catch (e) {
    // ignore storage errors
  }
}

export default function SilentMentorFeed() {
  const { nudges, loading } = useSilentMentor({ trigger: 'home_feed', placement: 'home_feed_card', limit: 2 });
  const navigate = useNavigate();
  const [visible, setVisible] = useState([]);
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated;

  useEffect(() => {
    if (!nudges) return;
    const visibleList = nudges.filter(n => !isSuppressed(n.id || n.nudgeId));
    setVisible(visibleList);
  }, [nudges]);

  const handleImpression = async (nudge) => {
    if (!isAuthenticated) return;
    try {
      await silentMentorApi.postEvent({
        eventType: 'nudge_shown',
        nudgeId: nudge.id || nudge.nudgeId,
        trigger: nudge.trigger || 'home_feed',
        placement: nudge.placement || 'home_feed_card',
        variant: nudge.variant,
      });
    } catch (e) {
      // ignore telemetry failures
    }
  };

  const handleClick = async (nudge) => {
    try {
      await silentMentorApi.postEvent({
        eventType: 'nudge_clicked',
        nudgeId: nudge.id || nudge.nudgeId,
        trigger: nudge.trigger || 'home_feed',
        placement: nudge.placement || 'home_feed_card',
        variant: nudge.variant,
      });
    } catch (e) {}

    // Micro-action started
    try {
      await silentMentorApi.postEvent({
        eventType: 'micro_action_started',
        nudgeId: nudge.id || nudge.nudgeId,
        actionType: nudge.action?.type || 'navigate',
        metadata: { target: nudge.action?.target },
      });
    } catch (e) {}

    // Navigate if target provided
    if (nudge.action && nudge.action.type === 'navigate' && nudge.action.target) {
      navigate(nudge.action.target);
    }
  };

  const handleDismiss = async (nudgeId) => {
    // Optimistic hide
    setVisible(v => v.filter(x => (x.id || x.nudgeId) !== nudgeId));
    // Persist suppression locally (7 days)
    suppressLocally(nudgeId, 7 * 24 * 60 * 60 * 1000);
    try {
      await silentMentorApi.postEvent({ eventType: 'nudge_dismissed', nudgeId });
    } catch (e) {}
  };

  const handleSnooze = async (nudgeId, hours = 24) => {
    setVisible(v => v.filter(x => (x.id || x.nudgeId) !== nudgeId));
    suppressLocally(nudgeId, hours * 60 * 60 * 1000);
    try {
      await silentMentorApi.postEvent({ eventType: 'nudge_snoozed', nudgeId, ttlHours: hours });
    } catch (e) {}
  };

  // Fire impressions for visible nudges on change
  useEffect(() => {
    visible.forEach(n => handleImpression(n));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (loading) return null;
  if (!visible || visible.length === 0) return null;

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto', my: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Tips for you</Typography>
      {visible.map((n) => (
        <div key={n.id || n.nudgeId}>
          <SilentMentorNudge
            nudgeId={n.id || n.nudgeId}
            title={n.title}
            message={n.message}
            actionLabel={n.action?.label}
            onAction={() => handleClick(n)}
            onDismiss={() => handleDismiss(n.id || n.nudgeId)}
            onSnooze={(hours) => handleSnooze(n.id || n.nudgeId, hours)}
            meta={{ variant: n.variant }}
          />
        </div>
      ))}
    </Box>
  );
}
